import * as React from 'react';
import { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import { top } from '../utils/customPropStyles';
import Badge from './Badge';
import Icon from './Icon';
import Translate, { translate } from './Translate';
import FilterSearch from './FilterSearch';
import FilterValue from './FilterValue';
import ClickLayer from './ClickLayer';
import { FilterItemProps } from '../models/components/Filter';
import { computed, observable } from 'mobx';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import {
    List,
    CellMeasurer,
    CellMeasurerCache,
    AutoSizer,
} from 'react-virtualized';

export interface RowItem {
    index: number;
    key: any;
    style: any;
    parent: any;
}

const FilterDropdownWrapper = styled<any, any>('div')`
    ${propStyles};
`;

FilterDropdownWrapper.defaultProps = {
    flex: ['0 0 100%', '0 0 33%', '0 0 20%'],
    maxWidth: ['100%', '33%', '20%'],
};

const FilterDropdownRow = styled<any, any>('div')`
    ${propStyles};
`;

const FilterListAutosizerWrapper = styled<any, any>('div')`
    ${propStyles};
`;

FilterListAutosizerWrapper.defaultProps = {
    height: '300px',
};

const FilterDropdownInnerWrapper = styled<any, any>('div')`
    ${propStyles};
`;

FilterDropdownInnerWrapper.defaultProps = {
    position: 'relative',
    marginX: 'lg',
    marginY: 'sm',
};

const FilterDropdownHeader = styled<any, any>('label')`
    ${propStyles};
`;

FilterDropdownHeader.defaultProps = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 'sm',
    fontFamily: 'heading',
    fontWeight: 'medium',
    color: 'body',
    margin: '0 0 0.5em 0',
};

const FilterDropdownButton = styled<any, any>('button')`
    ${propStyles};
    outline: none;
    cursor: pointer;
`;

FilterDropdownButton.defaultProps = {
    display: 'flex',
    flex: '0 0 auto',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: 'sm',
    fontSize: 'sm',
    fontFamily: 'body',
    fontWeight: 'normal',
    color: 'gray500',
    bgColor: 'white',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'solid',
    borderRadius: 'sm',
    hover: {
        borderColor: 'primary',
    },
    selected: {
        color: 'body',
        borderColor: 'primary',
    },
};

const FilterDropdownText = styled<any, any>('div')`
    ${propStyles};
    white-space: nowrap;
`;

FilterDropdownText.defaultProps = {
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: 'inherit',
};

const FilterDropdown = styled<any, any>('div')`
    ${propStyles};
    ${top};
`;

FilterDropdown.defaultProps = {
    position: 'absolute',
    top: 'calc(100% + 1px)',
    left: '0',
    right: '0',
    zIndex: 'dropdown',
    padding: 'md',
    bgColor: 'white',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'solid',
    borderRadius: 'sm',
    boxShadow: 'sm',
};

const ClearIndividualFiltersButton = styled<any, any>('button')`
    ${propStyles};
`;

ClearIndividualFiltersButton.defaultProps = {
    display: 'flex',
    fontFamily: 'body',
    fontWeight: 'normal',
    fontSize: 'sm',
    color: 'gray500',
    bgColor: 'transparent',
    borderStyle: 'none',
    cursor: 'pointer',
    outline: 'none',
    selected: {
        color: 'body',
    },
};

const rightAlignStyleProps = {
    order: 1,
    margin: '0 0 0 auto',
    zIndex: 1010,
};

const SelectedFilterInfo = styled<any, any>('div')`
    ${propStyles};
`;

SelectedFilterInfo.defaultProps = {
    display: 'flex',
};

const FilterList = styled<any, any>('ol')`
    ${propStyles};
    list-style: none;
    -ms-overflow-style: -ms-autohiding-scrollbar;
`;

FilterList.defaultProps = {
    maxHeight: '300px',
    overflowX: 'hidden',
    overflowY: 'auto',
};

const Divider = styled<any, any>('hr')`
    ${propStyles};
` as any; // TODO: why?!

Divider.defaultProps = {
    borderWidth: '1px',
    borderColor: 'gray500',
    borderStyle: 'dotted none none none',
};

@inject('translationStore')
@inject('filterStore')
@inject('accessibilityStore')
@observer
class FilterItemDropdown extends Component<FilterItemProps> {
    @observable updated = 1; // Setting this forces the autosizer to re-render when the component props change
    @observable cache: CellMeasurerCache;

    constructor(props) {
        super(props);
        this.cache = new CellMeasurerCache({
            fixedWidth: true,
            defaultHeight: props.defaultRowHeight || 100,
        });
    }

    @computed
    get showClear() {
        const { search } = this.props;
        return search.length > 0;
    }

    @computed
    get filteredValues() {
        return this.props && this.props.filteredValues;
    }

    @computed
    get mustVirtualize() {
        const { filter } = this.props;
        return filter.dropdownOptions && filter.dropdownOptions.virtualize;
    }

    renderRow = ({ index, key, style, parent }: RowItem) => {
        const { filterStore, filter, toggleExpanded, theme } = this.props;
        const filteredValue = this.filteredValues[index];
        const active = filterStore.isActive(
            filter.property,
            filteredValue.value
        );
        const onChange = e => {
            filterStore.toggleFilter(
                filter.property,
                filteredValue.value,
                filter.filterSearchKey,
                false,
                filter
            );
            if (filter.ui === 'dropdown') {
                toggleExpanded(e);
            }
        };

        return (
            <CellMeasurer
                key={key}
                cache={this.cache}
                parent={parent}
                columnIndex={0}
                rowIndex={index}
            >
                <FilterDropdownRow key={key} style={style}>
                    <FilterValue
                        key={`${filteredValue.label}-${index}`}
                        label={filteredValue.label}
                        ui={filter.ui}
                        value={filteredValue.value}
                        checked={false}
                        onChange={onChange}
                        active={active}
                        filterProperty={filter.property}
                        {...theme.FilterDropdownFilterListItem}
                    />
                </FilterDropdownRow>
            </CellMeasurer>
        );
    };

    @computed
    get autosizeFn() {
        const overscanRowCount = 10;
        return ({ width, height }: { width: any; height: any }) => {
            return (
                <List
                    updated={this.updated++}
                    width={width}
                    height={height}
                    rowHeight={this.cache.rowHeight}
                    rowRenderer={this.renderRow}
                    rowCount={this.filteredValues.length}
                    overscanRowCount={overscanRowCount}
                />
            );
        };
    }

    render() {
        const {
            theme,
            translationStore,
            filterStore,
            accessibilityStore,
            filter,
            // withFilter HoC props
            expanded,
            toggleExpanded,
            selectedCount,
            total,
            search,
            clearSearch,
            selectedValues,
            filteredValues,
            filterSearch,
            enableSearch,
            filterDropdownText,
            isRequired = false,
        } = this.props;
        const isDropdown = filter.ui === 'dropdown';
        const dropdownId = filter.label.toLowerCase().replace(/\s/g, '-');

        return (
            <FilterDropdownWrapper {...theme.FilterDropdownWrapper}>
                <FilterDropdownInnerWrapper
                    {...theme.FilterDropdownInnerWrapper}
                >
                    <FilterDropdownHeader
                        htmlFor={dropdownId}
                        {...theme.FilterDropdownHeader}
                    >
                        <Translate>{filter.label}</Translate>
                        {selectedCount > 0 && (
                            <SelectedFilterInfo>
                                {filterStore.clearIndividualFilters.enabled && (
                                    <ClearIndividualFiltersButton
                                        aria-label={accessibilityStore.buttonAccessibilityValue(
                                            'ClearIndividualFiltersButton'
                                        )}
                                        {...(filterStore.clearIndividualFilters
                                            .align === 'right' &&
                                            rightAlignStyleProps)}
                                        {...theme.ClearIndividualFiltersButton}
                                        onClick={
                                            filterStore.filtersActive
                                                ? e => {
                                                      filterStore.clearFiltersOfType(
                                                          filter.property,
                                                          filter.filterSearchKey
                                                      );
                                                      if (expanded) {
                                                          toggleExpanded(e);
                                                      }
                                                  }
                                                : () => undefined
                                        }
                                        data-metric-id={`filterItem-clear-${camelCase(
                                            filter.property
                                        )}`}
                                    >
                                        <Icon
                                            height="1.4em"
                                            width="1.4em"
                                            margin="0 0.25em 0 0.25em"
                                            fileName="ClearFiltersIcon"
                                            {...theme.ClearFiltersIcon}
                                        />
                                    </ClearIndividualFiltersButton>
                                )}
                                {!isDropdown && (
                                    <Badge
                                        fontSize="0.75em"
                                        data-selected={selectedCount > 0}
                                        selected={{
                                            bgColor: 'primary',
                                        }}
                                        {...theme.FilterDropdownTotalBadge}
                                    >
                                        {selectedCount}/{total}
                                    </Badge>
                                )}
                            </SelectedFilterInfo>
                        )}
                    </FilterDropdownHeader>
                    <FilterDropdownButton
                        aria-label={accessibilityStore.buttonAccessibilityValue(
                            'FilterDropdownButton'
                        )}
                        id={dropdownId}
                        onClick={toggleExpanded}
                        title={filterDropdownText}
                        data-selected={expanded || selectedCount > 0}
                        data-metric-id={`filterItemDropdown-button-${camelCase(
                            filter.property
                        )}`}
                        {...theme.FilterDropdownButton}
                        {...(isRequired
                            ? theme.FilterDropdownButtonRequired
                            : {})}
                    >
                        <FilterDropdownText {...theme.FilterDropdownText}>
                            {filterDropdownText}
                        </FilterDropdownText>
                        <Icon
                            fileName={
                                expanded
                                    ? 'FilterDropdownIconExpanded'
                                    : 'FilterDropdownIcon'
                            }
                            {...theme.FilterDropdownIcon}
                        />
                    </FilterDropdownButton>
                    {expanded && (
                        <Fragment>
                            <ClickLayer
                                data-metric-id={'filterItemDropdown-clickLayer'}
                                onClick={toggleExpanded}
                            />
                            <FilterDropdown {...theme.FilterDropdown}>
                                {enableSearch && (
                                    <FilterSearch
                                        type="text"
                                        placeholder={`${translate(
                                            translationStore,
                                            'Search'
                                        )} ${translate(
                                            translationStore,
                                            filter.label
                                        )}`}
                                        value={search}
                                        onChange={e =>
                                            filterSearch(e.target.value)
                                        }
                                        onClear={clearSearch}
                                        showClear={this.showClear}
                                        {...theme.FilterDropdownSearch}
                                    />
                                )}
                                <FilterList
                                    {...theme.FilterDropdownSelectedFilterList}
                                >
                                    {selectedValues.map((value, valueIndex) => (
                                        <FilterValue
                                            key={`${value.label}-${valueIndex}`}
                                            label={value.label}
                                            ui={filter.ui}
                                            value={value.value}
                                            checked={true}
                                            onChange={() =>
                                                filterStore.toggleFilter(
                                                    filter.property,
                                                    value.value,
                                                    filter.filterSearchKey,
                                                    true,
                                                    filter
                                                )
                                            }
                                            active={true}
                                            filterProperty={filter.property}
                                            {...theme.FilterDropdownSelectedFilterListItem}
                                        />
                                    ))}
                                </FilterList>
                                {selectedValues.length > 0 &&
                                    filteredValues.length > 0 && (
                                        <Divider
                                            {...theme.FilterDropdownFilterListDivider}
                                        />
                                    )}
                                <FilterList {...theme.FilterDropdownFilterList}>
                                    {this.mustVirtualize && (
                                        <FilterListAutosizerWrapper>
                                            <AutoSizer
                                                children={this.autosizeFn}
                                            />
                                        </FilterListAutosizerWrapper>
                                    )}
                                    {!this.mustVirtualize &&
                                        filteredValues.reduce(
                                            (
                                                acc,
                                                filteredValue,
                                                valueIndex
                                            ) => {
                                                const active = filterStore.isActive(
                                                    filter.property,
                                                    filteredValue.value
                                                );
                                                if (
                                                    !isDropdown ||
                                                    !filter.dropdownOptions ||
                                                    !filter.dropdownOptions
                                                        .hideInactive ||
                                                    active
                                                ) {
                                                    const key = `${filteredValue.label}-${valueIndex}`;

                                                    const onChange = e => {
                                                        filterStore.toggleFilter(
                                                            filter.property,
                                                            filteredValue.value,
                                                            filter.filterSearchKey,
                                                            false,
                                                            filter
                                                        );
                                                        if (
                                                            filter.ui ===
                                                            'dropdown'
                                                        ) {
                                                            toggleExpanded(e);
                                                        }
                                                    };

                                                    acc.push(
                                                        <FilterValue
                                                            key={key}
                                                            label={
                                                                filteredValue.label
                                                            }
                                                            ui={filter.ui}
                                                            value={
                                                                filteredValue.value
                                                            }
                                                            checked={false}
                                                            onChange={onChange}
                                                            active={active}
                                                            filterProperty={
                                                                filter.property
                                                            }
                                                            {...theme.FilterDropdownFilterListItem}
                                                        />
                                                    );
                                                }
                                                return acc;
                                            },
                                            []
                                        )}
                                </FilterList>
                            </FilterDropdown>
                        </Fragment>
                    )}
                </FilterDropdownInnerWrapper>
            </FilterDropdownWrapper>
        );
    }
}

export default withTheme(FilterItemDropdown);



// WEBPACK FOOTER //
// ./src/Components/FilterItemDropdown.tsx